import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Form, Field, FieldProps, FieldRenderProps } from 'react-final-form';
import { Popover, withStyles } from '@material-ui/core';

const styles = {
  paper: {
    zIndex: 99999,
    borderRadius: '8px'
  }
};

const Wrapper = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.4);
  }
`;

const Dropdown = styled.div`
  z-index: 50;
  background: linear-gradient(0deg, #1e1e1e, #1e1e1e),
    linear-gradient(0deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.16));

  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  width: 280px;
`;

const Header = styled.div`
  display: flex;
  color: white;
`;

const HeaderSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-right: ${({ hasBorder }) => (hasBorder ? '1px solid #4b5563' : 'none')};
  border-bottom: 2px solid #3994de;
`;

const HeaderLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const ScrollButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScrollButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 2px;
  cursor: pointer;
  color: white;
  transition: background 0.2s ease;

  &:hover {
    background: #4b5563;
  }
`;

const ListContainer = styled.div`
  display: flex;
  height: 240px;
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  border-right: ${({ hasBorder }) => (hasBorder ? '1px solid #4b5563' : 'none')};
`;

const ListItem = styled.button`
  width: 100%;
  padding: 8px 16px;
  text-align: left;
  font-size: 14px;
  background: ${({ active }) => (active ? '#2563eb' : 'transparent')};
  color: ${({ active }) => (active ? 'white' : '#e5e7eb')};
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ active }) => (active ? '#2563eb' : '#4b5563')};
  }
`;

type Props = FieldRenderProps<string> & {
  anchorEl: HTMLDivElement | null;
  close: () => void;
  afterChange?: (newValue: string) => void | Promise<void>;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 13 }, (_, i) => currentYear + 2 - i);

const CalendarFormPopup = ({ input, anchorEl, close, classes, afterChange }: Props) => {
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth());
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  const monthSelected = useRef(false);
  const yearSelected = useRef(false);

  useEffect(() => {
    if (input.value) {
      const [year, month] = input.value.split('-');
      setDisplayMonth(parseInt(month) - 1);
      setDisplayYear(parseInt(year));
    }
  }, [input.value]);

  const handleSelect = async (month, year, type) => {
    const value = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    input.onChange(value);
    if (afterChange) {
      await afterChange(value);
    }
    if (type === 'month') {
      monthSelected.current = true;
    }
    if (type === 'year') {
      yearSelected.current = true;
    }
    if (yearSelected.current && monthSelected.current) {
      close();
    }
  };

  useEffect(() => {
    if (!anchorEl) {
      monthSelected.current = false;
      yearSelected.current = false;
    }
  }, [anchorEl]);

  return (
    <Popover
      id="calendar-popover"
      open={!!anchorEl}
      classes={{ paper: classes.paper }}
      anchorEl={anchorEl}
      onClose={close}
      onBackdropClick={close}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      style={{ zIndex: 9999999 }}
    >
      <Dropdown>
        <Header>
          <HeaderSection hasBorder>
            <HeaderLabel>{months[displayMonth]}</HeaderLabel>
            <ScrollButtons>
              {/* <ScrollButton onClick={() => scrollMonth(1)}>
                  <ChevronUp size={16} />
                </ScrollButton>
                <ScrollButton onClick={() => scrollMonth(-1)}>
                  <ChevronUp size={16} style={{ transform: 'rotate(180deg)' }} />
                </ScrollButton> */}
            </ScrollButtons>
          </HeaderSection>

          <HeaderSection>
            <HeaderLabel>{displayYear}</HeaderLabel>
            <ScrollButtons>
              {/* <ScrollButton onClick={() => scrollYear(1)}>
                  <ChevronUp size={16} />
                </ScrollButton>
                <ScrollButton onClick={() => scrollYear(-1)}>
                  <ChevronUp size={16} style={{ transform: 'rotate(180deg)' }} />
                </ScrollButton> */}
            </ScrollButtons>
          </HeaderSection>
        </Header>

        <ListContainer>
          <List hasBorder>
            {months.map((month, i) => (
              <ListItem
                key={month}
                active={input.value === `${displayYear}-${String(i + 1).padStart(2, '0')}-01`}
                onClick={() => handleSelect(i, displayYear, 'month')}
              >
                {month}
              </ListItem>
            ))}
          </List>
          <List>
            {years.map(year => (
              <ListItem
                key={year}
                active={input.value === `${year}-${String(displayMonth + 1).padStart(2, '0')}-01`}
                onClick={() => handleSelect(displayMonth, year, 'year')}
              >
                {year}
              </ListItem>
            ))}
          </List>
        </ListContainer>
      </Dropdown>
    </Popover>
  );
};

export default withStyles(styles as any)(CalendarFormPopup);
